import type { FC, ReactNode } from 'react';
import { Fragment, Suspense, useEffect } from 'react';

import { LoadingScreen } from '@truvhq/ui';
import type { RouteComponentProps } from 'react-router-dom';
import { Redirect, Route, Switch } from 'react-router-dom';

import type { Permission } from 'api/types';
import { startBackgroundPrefetch } from 'app/routes/routePrefetch';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { urls } from 'shared/urls';

export type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: FC<{ children: ReactNode }>;
  layout?: FC<{ children: ReactNode }>;
  component?: FC<RouteComponentProps>;
  routes?: Routes;
  permission?: Permission | Permission[];
  companyPermission?: Permission;
  /**
   * Sends a phone to the orders list instead. For a page built around a wide table or a chart
   * grid, which a phone has no useful way to show and no nav entry to reach.
   */
  desktopOnly?: boolean;
}[];

type RenderRoutesProps = {
  routes?: Routes;
  defaultRedirectUrl: string;
};

export const RenderRoutes: FC<RenderRoutesProps> = ({ routes = [], defaultRedirectUrl }) => {
  const { isMobile } = useScreenSize();

  useEffect(() => {
    startBackgroundPrefetch();
  }, []);

  return (
    <Switch>
      {routes.map((route) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component || Fragment;
        const key = route.path?.toString() || '';
        const guardProps: {
          permission?: Permission | Permission[];
          companyPermission?: Permission;
          defaultRedirectUrl?: string;
        } = {};

        if (route.guard) {
          guardProps.permission = route.permission;
          guardProps.companyPermission = route.companyPermission;
          guardProps.defaultRedirectUrl = defaultRedirectUrl;
        }

        return (
          <Route
            key={key}
            exact={route.exact}
            path={route.path}
            render={(props) =>
              route.desktopOnly && isMobile ? (
                <Redirect to={urls.orders.url()} />
              ) : (
                <Guard {...guardProps}>
                  <Layout>
                    {route.routes ? (
                      <RenderRoutes defaultRedirectUrl={defaultRedirectUrl} routes={route.routes} />
                    ) : (
                      <Suspense fallback={<LoadingScreen className="min-h-screen" />}>
                        <Component {...props} />
                      </Suspense>
                    )}
                  </Layout>
                </Guard>
              )
            }
          />
        );
      })}
    </Switch>
  );
};
